import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, Mail } from 'lucide-react';
import recruiterService, { QuotaBalance } from '@/services/recruiterService';

function QuotaMeter({
  icon,
  label,
  remaining,
  allowed,
}: {
  icon: React.ReactNode;
  label: string;
  remaining: number;
  allowed: number;
}) {
  const { t } = useTranslation('recruiter');
  if (allowed === 0) return null;

  const usedPct = Math.min(Math.round(((allowed - remaining) / allowed) * 100), 100);
  const remainingPct = Math.round((remaining / allowed) * 100);

  const barColor =
    remainingPct > 40 ? 'bg-blue-500' : remainingPct > 15 ? 'bg-amber-500' : 'bg-red-500';
  const textColor =
    remainingPct > 40 ? 'text-gray-700' : remainingPct > 15 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="flex items-center gap-2">
      {icon}
      <span className="text-xs text-gray-500 shrink-0">{label}</span>
      <div className="w-16 bg-gray-100 rounded-full h-1.5 shrink-0">
        <div
          className={`h-1.5 rounded-full transition-all duration-300 ${barColor}`}
          style={{ width: `${usedPct}%` }}
        />
      </div>
      <span className={`text-xs font-semibold tabular-nums shrink-0 ${textColor}`}>
        {t('analytics.usage.quota.usedOf', { used: remaining, allowed })}
      </span>
    </div>
  );
}

export default function QuotaBar() {
  const { t } = useTranslation('recruiter');
  const [quota, setQuota] = useState<QuotaBalance | null>(null);

  useEffect(() => {
    recruiterService.getQuota().then(setQuota).catch(() => {});
  }, []);

  if (!quota) return null;

  return (
    <div className="w-full bg-white border-b border-gray-100 shadow-sm">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 container-custom py-2">
        <span className="text-xs font-medium text-gray-400 shrink-0">
          {t('analytics.usage.quota.title')}
        </span>
        <QuotaMeter
          icon={<Zap className="w-3.5 h-3.5 text-blue-500 shrink-0" />}
          label={t('analytics.usage.types.AI_MATCH')}
          remaining={quota.aiMatchesRemaining}
          allowed={quota.aiMatchesAllowed}
        />
        <QuotaMeter
          icon={<Mail className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
          label={t('analytics.usage.types.CONTACT_REVEAL')}
          remaining={quota.contactRevealsRemaining}
          allowed={quota.contactRevealsAllowed}
        />
      </div>
    </div>
  );
}
